import { Component, EventEmitter, Input, Output } from '@angular/core';
import { I18nStringItem } from '@tj/tj-dmp-lib-api';
import { TranslateService } from '@ngx-translate/core';
import { FormatStringService } from '../../../../../services/format-string.service';
import { PreviewDevice } from '../../../../../components/simple-iframe/simple-iframe.component';
import { TRANSLATION_LANGS } from '../../../components/translation-manager/translation-manager.component';

/**
 * Atelier d'un bloc HTML (cf. docs/ui-design-guidelines.md §17) : le code à gauche, l'aperçu à droite, les
 * traductions du bloc déjà remplacées dans la langue choisie.
 */
@Component({
    selector: 'dmp-html-web-editor',
    templateUrl: './html-web-editor.component.html',
    styleUrls: ['./html-web-editor.component.scss'],
    standalone: false
})
export class HtmlWebEditorComponent {

	readonly langs = TRANSLATION_LANGS.map(lang => ({label: lang, value: lang}));
	readonly devices: { label: string; value: PreviewDevice; icon: string }[];

	editorOptions = {
		theme: 'vs',
		lineNumbers: 'on',
		language: 'html',
		readOnly: false,
		minimap: {enabled: false},
		scrollBeyondLastLine: false,
		// L'éditeur suit la largeur de sa colonne quand il réapparaît à côté de l'aperçu.
		automaticLayout: true,
	};

	/** Le code édité. */
	@Input() code = '';
	@Output() codeChange = new EventEmitter<string>();

	/** Ce que montre l'aperçu : le code édité, ou le contenu par défaut quand l'affilié le reprend. */
	@Input() previewCode = '';

	/** Masqué (et non détruit) : l'éditeur garde son historique d'annulation. */
	@Input() showEditor = true;

	@Input() translations: I18nStringItem[] = [];

	@Output() onInitEditor = new EventEmitter();

	lang: string = TRANSLATION_LANGS[0];
	device: PreviewDevice = 'desktop';

	constructor(private formatService: FormatStringService, translate: TranslateService) {
		this.devices = [
			{label: translate.instant('htmlBlockEdit.preview.mobile'), value: 'mobile', icon: 'fas fa-mobile-alt'},
			{label: translate.instant('htmlBlockEdit.preview.tablet'), value: 'tablette', icon: 'fas fa-tablet-alt'},
			{label: translate.instant('htmlBlockEdit.preview.desktop'), value: 'desktop', icon: 'fas fa-desktop'},
		];
	}

	/** Comme le configurateur à l'envoi : une langue absente de la traduction reprend le français. */
	get translationsObject(): Record<string, string> {
		return (this.translations ?? []).reduce((acc, curr) => ({
			...acc,
			[curr.key]: this.lang in curr.values ? curr.values[this.lang] : curr.values[TRANSLATION_LANGS[0]] ?? ''
		}), {} as Record<string, string>);
	}

	get previewTranslated(): string {
		return this.formatService.format(this.previewCode, this.translationsObject);
	}

	handleChange(code: string) {
		if (code === this.code) {
			return;
		}
		this.code = code;
		this.codeChange.emit(code);
	}

	initMonaco(editor) {
		this.onInitEditor.emit(editor);
	}
}
